import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import { Tag } from '../types/tag.type';
import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { ADMIN_TAGS_API_PATHS } from '../constants/tags-api-paths.constants';

type UseAdminGetTagsResult = {
  isLoading: boolean;
  isRefetching: boolean;
  tags: Tag[];
  totalCount: number;
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetTagsResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  tags: [],
  totalCount: 0,
};

const QUERY_PARAMS = { page: 1, limit: 1000, sortBy: 'name', sortDirection: 'asc' };

export function useAdminGetTags(): UseAdminGetTagsResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<{ totalCount: number; tags: Tag[] }>(ADMIN_TAGS_API_PATHS.GET_TAGS, {
        params: QUERY_PARAMS,
      });

      setResult({ isLoading: false, isRefetching: false, tags: response.tags, totalCount: response.totalCount });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(error, 'Une erreur est survenue lors de la récupération des tags'),
        { variant: 'error' }
      );
      setResult({ isLoading: false, isRefetching: false, tags: [], totalCount: 0 });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
